import React, { useCallback, useRef } from 'react';
import styled from 'styled-components';
import { Upload } from 'lucide-react';
import { SMART_SORTING_ACCEPT } from '../lib/file';

const DropZone = styled.div<{ $isDragActive: boolean; $compact: boolean }>`
  border: 1px dashed
    ${(props) => (props.$isDragActive ? 'var(--brand-500)' : 'var(--border)')};
  border-radius: var(--radius-lg);
  background: ${(props) =>
    props.$isDragActive
      ? 'var(--brand-50, rgba(59, 130, 246, 0.05))'
      : 'var(--surface)'};
  padding: ${(props) => (props.$compact ? '1.25rem' : '2.5rem')};
  text-align: center;
  cursor: pointer;
  transition: all var(--transition-fast);

  &:hover {
    border-color: var(--brand-400);
    background: var(--interactive-hover, rgba(0, 0, 0, 0.02));
  }
`;

const DropLabel = styled.p<{ $compact: boolean }>`
  font-size: ${(props) => (props.$compact ? '0.9rem' : '1rem')};
  color: var(--text-secondary);
  margin: 0;
`;

const DropIcon = styled.span`
  display: inline-flex;
  align-items: center;
  margin-right: 0.5rem;
  vertical-align: middle;
  color: var(--text-muted);

  svg {
    width: 1.25rem;
    height: 1.25rem;
    stroke-width: 2;
  }
`;

const BrowseButton = styled.button`
  background: var(--action-primary);
  color: white;
  border: none;
  border-radius: var(--radius-md);
  padding: 0.5rem 1.25rem;
  font-weight: 500;
  cursor: pointer;
  margin-top: 0.75rem;
  transition: background var(--transition-fast);

  &:hover {
    background: var(--action-primary-hover);
  }
`;

const HiddenInput = styled.input`
  display: none;
`;

const Hint = styled.p`
  font-size: 0.8rem;
  color: var(--text-muted);
  margin: 0.5rem 0 0 0;
`;

interface SmartSortingUploadPanelProps {
  compact?: boolean;
  onFilesSelected: (files: File[]) => void;
}

export const SmartSortingUploadPanel: React.FC<
  SmartSortingUploadPanelProps
> = ({ compact = false, onFilesSelected }) => {
  const [isDragActive, setIsDragActive] = React.useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const acceptString = Object.keys(SMART_SORTING_ACCEPT).join(',');

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!isDragActive) setIsDragActive(true);
    },
    [isDragActive],
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragActive(false);

      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        onFilesSelected(files);
      }
    },
    [onFilesSelected],
  );

  const handleClick = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files ?? []);
      if (files.length > 0) {
        onFilesSelected(files);
      }
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    },
    [onFilesSelected],
  );

  return (
    <div>
      <DropZone
        $isDragActive={isDragActive}
        $compact={compact}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
        onKeyDown={(event) => {
          if (
            (event.key === 'Enter' || event.key === ' ') &&
            event.target === event.currentTarget
          ) {
            event.preventDefault();
            handleClick();
          }
        }}
        role="button"
        tabIndex={0}
        aria-label="Subir archivos para clasificación"
      >
        <DropLabel $compact={compact}>
          <DropIcon aria-hidden>
            <Upload />
          </DropIcon>
          {compact
            ? 'Arrastra archivos aquí o haz clic para seleccionar'
            : 'Arrastra uno o varios archivos aquí'}
        </DropLabel>
        {!compact && (
          <>
            <BrowseButton type="button">Seleccionar archivos</BrowseButton>
            <Hint>
              PDF, JPG, PNG, TXT, CSV, XLSX, XLSM — Máximo 10 MB por archivo
            </Hint>
          </>
        )}
      </DropZone>
      <HiddenInput
        ref={inputRef}
        type="file"
        accept={acceptString}
        multiple
        onChange={handleInputChange}
        aria-hidden
        tabIndex={-1}
      />
    </div>
  );
};
